import cx from 'classnames';
import {useCallback, useEffect, useRef, useState} from 'react';
import {getOrientation} from '~/common/FabricInvitation/EventCreateDesign/hooks/useOrientation';
import {useCachedMedia} from '~/common/hooks/useCachedMedia';
import {useIsMediumAndUp} from '~/common/hooks/useMatchQuery';
import {
  GALLERY_PHOTO_PREVIEW_INDEXED_DB_STORE_NAME,
  GALLERY_PHOTO_PREVIEW_MEDIA_ID,
} from '~/common/indexed-db/dexieStores';
import {MaskingFilter} from '~/GalleryPB/components/MaskingFilter/MaskingFilter';
import {useUyoOrDyo} from '~/GalleryPB/hooks/useUyoOrDyo';
import {GalleryAssetName} from '~/GalleryPB/utils';
import linerShading from '~images/liner_shading.png';
import {Video} from '~library/atoms/Video';
import {useBrowserHeight} from '~library/hooks/useBrowserHeight';
import {useBrowserWidth} from '~library/hooks/useBrowserWidth';

import './PremiumAnimationB.sass';

const HEIGHT_TO_WIDTH_RATIO = 1.36;

export const PremiumAnimationB = ({
  template,
  templateSrc,
  skipAnimation,
  liner,
  envelopeClosedBack,
  envelopeOpen,
  onAssetsLoaded,
  style,
  noShadowOnCard = false,
}) => {
  const {getCachedMediaDataById} = useCachedMedia(GALLERY_PHOTO_PREVIEW_INDEXED_DB_STORE_NAME);
  const media = getCachedMediaDataById(GALLERY_PHOTO_PREVIEW_MEDIA_ID);
  const hasMedia = !!media;
  const isMediumScreenAndUp = useIsMediumAndUp();
  const {isInUyoOrDyo} = useUyoOrDyo();

  const inviteContainerRef = useRef(null);
  const envelopeOpenFlapRef = useRef(null);
  const envelopeClosedFlapRef = useRef(null);
  const envelopeRef = useRef(null);
  const linerRef = useRef(null);
  const linerShadingRef = useRef(null);
  const yourInvitationRef = useRef(null);
  const templateRef = useRef(null);

  const browserWidth = useBrowserWidth();
  const browserHeight = useBrowserHeight();

  const [envelopeLoaded, setEnvelopeLoaded] = useState(false);
  const [cardLoaded, setCardLoaded] = useState(false);
  const [linerShadingLoaded, setLinerShadingLoaded] = useState(false);
  const [linerLoaded, setLinerLoaded] = useState(false);
  const [envelopeCloseFlapLoaded, setEnvelopeCloseFlapLoaded] = useState(false);

  const isFree = Boolean(skipAnimation);

  const assetsLoaded = isFree
    ? cardLoaded
    : envelopeLoaded && cardLoaded && linerShadingLoaded && linerLoaded && envelopeCloseFlapLoaded;

  const isTemplateSrcAVideo = templateSrc.endsWith('mp4');
  // TODO: remove templateDefaultLegacy after Release 1718 is out: ticket EVT-34841
  const templateDefaultLegacy = isMediumScreenAndUp
    ? GalleryAssetName.desktop.uyo_mask_legacy
    : GalleryAssetName.mobile.uyo_mask_legacy;
  const templateDefault = isMediumScreenAndUp
    ? GalleryAssetName.desktop.uyo_mask
    : GalleryAssetName.mobile.uyo_mask;

  const templateOrientation = getOrientation(template.width, template.height);

  useEffect(() => {
    if (assetsLoaded && typeof onAssetsLoaded === 'function') onAssetsLoaded();
  }, [assetsLoaded, onAssetsLoaded]);

  useEffect(() => {
    if (isFree) return;

    const envelopeAnimation = () => {
      window.setTimeout(() => {
        inviteContainerRef?.current?.classList.remove('step-0');
        inviteContainerRef?.current?.classList.add('step-1');

        window.setTimeout(() => {
          inviteContainerRef?.current?.classList.remove('step-1');
          inviteContainerRef?.current?.classList.add('step-2');

          window.setTimeout(() => {
            inviteContainerRef?.current?.classList.remove('step-2');
            inviteContainerRef?.current?.classList.add('step-3');

            window.setTimeout(() => {
              inviteContainerRef?.current?.classList.remove('step-3');
              inviteContainerRef?.current?.classList.add('step-4');

              window.setTimeout(() => {
                inviteContainerRef?.current?.classList.remove('step-4');
                inviteContainerRef?.current?.classList.add('step-0');
              }, 460);
            }, 1960);
          }, 460);
        }, 460);
      }, 920);
    };
    let interval;

    if (assetsLoaded) {
      envelopeAnimation();
      interval = setInterval(() => {
        envelopeAnimation();
      }, 5600);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [assetsLoaded, isFree]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (!yourInvitationRef.current) return;

    const width = yourInvitationRef.current.offsetHeight / HEIGHT_TO_WIDTH_RATIO;

    templateRef.current.style.width = `${width}px`;
    templateRef.current.style.right = `calc((100% - ${width}px) / 2)`;
    if (!isFree) {
      envelopeRef.current.style.width = `${width}px`;
      envelopeRef.current.style.left = `calc((100% - ${width}px) / 2)`;
      envelopeOpenFlapRef.current.style.width = `${width}px`;
      envelopeOpenFlapRef.current.style.left = `calc((100% - ${width}px) / 2)`;
      envelopeClosedFlapRef.current.style.width = `${width}px`;
      envelopeClosedFlapRef.current.style.left = `calc((100% - ${width}px) / 2)`;
      linerRef.current.style.width = `${width}px`;
      linerRef.current.style.left = `calc((100% - ${width}px) / 2)`;
      linerShadingRef.current.style.width = `${width}px`;
      linerShadingRef.current.style.left = `calc((100% - ${width}px) / 2)`;
    }
  }, [browserWidth, browserHeight, isFree]);

  const onEnvelopeLoaded = useCallback(() => setEnvelopeLoaded(true), []);

  const onCardLoaded = useCallback(() => setCardLoaded(true), []);

  const onLinerShadingLoaded = useCallback(() => setLinerShadingLoaded(true), []);

  const onLinerLoaded = useCallback(() => setLinerLoaded(true), []);

  const onEnvelopeCloseFlapLoaded = useCallback(() => setEnvelopeCloseFlapLoaded(true), []);

  return (
    <div
      className="invite-container variant-B step-0 fabric" // TODO: Rebuild animation
      ref={inviteContainerRef}
      style={style}
    >
      {!isFree && (
        <>
          <div className="invite-animation open-flap">
            <div
              className={`animation-image ${assetsLoaded ? 'ready' : ''} invite-envelope__open-flap-group`}
              ref={envelopeOpenFlapRef}
            >
              <img
                className="invite-envelope__open-flap invite-envelope__open-flap-shading"
                src={linerShading}
                alt=""
                onLoad={onLinerShadingLoaded}
              />
              <img
                className="invite-envelope__open-flap"
                src={envelopeOpen}
                alt=""
                onLoad={onEnvelopeLoaded}
              />
              <img
                className="invite-envelope__top-liner"
                src={liner}
                alt=""
                onLoad={onLinerLoaded}
              />
            </div>
          </div>

          <div className="invite-animation open-bottom">
            <img
              className={cx(
                'animation-image',
                {ready: assetsLoaded},
                'invite-envelope__bottom-liner'
              )}
              src={liner}
              ref={linerRef}
              alt=""
              onLoad={onLinerLoaded}
            />
            <img
              className={cx(
                'animation-image',
                {ready: assetsLoaded},
                'invite-envelope__bottom-liner-shading'
              )}
              src={linerShading}
              ref={linerShadingRef}
              onLoad={onLinerShadingLoaded}
              alt=""
            />
          </div>
        </>
      )}

      <div className="invite-animation your-invitation" ref={yourInvitationRef}>
        <div
          className={cx(
            'animation-image',
            {ready: assetsLoaded},
            'invite-envelope__your-invitation',
            'fabric'
          )}
        >
          <div className="invite-envelope__your-invitation__card" ref={templateRef}>
            {isTemplateSrcAVideo &&
              (hasMedia &&
              isInUyoOrDyo &&
              (template.dyo || template.uyo) &&
              !template.templateId.endsWith('_uyol')
                ? <MaskingFilter
                    media={media}
                    templateDefault={templateSrc}
                    uyoPosition={template.uyoImagePosition}
                    isMobile={!isMediumScreenAndUp}
                    onLoad={onCardLoaded}
                    isVideo
                    templateOrientation={templateOrientation}
                  />
                : <Video
                    src={templateSrc}
                    className={cx(`fabric_user_card`, {
                      ready: assetsLoaded,
                      'fabric_user_card--no-shadow': noShadowOnCard,
                    })}
                    alt=""
                    onCanPlay={onCardLoaded}
                  />)}
            {!isTemplateSrcAVideo &&
              (hasMedia &&
              isInUyoOrDyo &&
              (template.dyo || template.uyo) &&
              !template.templateId.endsWith('_uyol')
                ? <MaskingFilter
                    media={media}
                    templateDefault={
                      template?.assets?.default[templateDefault] ||
                      template?.assets?.default[templateDefaultLegacy]
                    }
                    uyoPosition={template.uyoImagePosition}
                    isMobile={!isMediumScreenAndUp}
                    onLoad={onCardLoaded}
                  />
                : <img
                    src={templateSrc}
                    className={cx('fabric_user_card', {
                      ready: assetsLoaded,
                      'fabric_user_card--no-shadow': noShadowOnCard,
                    })}
                    alt=""
                    onLoad={onCardLoaded}
                  />)}
          </div>
        </div>
      </div>

      {!isFree && (
        <>
          <div className="invite-animation open-bottom-front">
            <img
              className={cx(
                'animation-image',
                {ready: assetsLoaded},
                'invite-envelope__open-bottom-front'
              )}
              src={envelopeOpen}
              ref={envelopeRef}
              onLoad={onEnvelopeLoaded}
              alt=""
            />
          </div>

          <div className="invite-animation closed-flap">
            <img
              className={cx(
                'animation-image',
                {ready: assetsLoaded},
                'invite-envelope__closed-flap'
              )}
              src={envelopeClosedBack}
              ref={envelopeClosedFlapRef}
              alt=""
              onLoad={onEnvelopeCloseFlapLoaded}
            />
          </div>
        </>
      )}
    </div>
  );
};
